import React, { useState } from 'react';
import PasswordInput from '../PasswordInput';

const LoginView = ({
  loginData,
  setLoginData,
  loginError,
  isLoading,
  handleLogin,
  requiresTOTP,
  requiresTOTPSetup,
  requiresPasswordChange,
  setRequiresPasswordChange,
  forcePasswordForm,
  setForcePasswordForm,
  handleForcePasswordChange,
  totpToken,
  setTotpToken,
  backupCode,
  setBackupCode,
  useBackupCode,
  setUseBackupCode,
  totpSetupData,
  setupTOTP,
  verifyTOTPSetup,
  resetTOTP,
}) => {
  const newPass = forcePasswordForm?.new_password || '';
  const confirmPass = forcePasswordForm?.confirm_password || '';

  // Live password complexity checks
  const hasLength = newPass.length >= 12;
  const hasUpper = /[A-Z]/.test(newPass);
  const hasLower = /[a-z]/.test(newPass);
  const hasNumber = /[0-9]/.test(newPass);
  const hasSpecial = /[^A-Za-z0-9]/.test(newPass);
  const isMatching = newPass.length > 0 && newPass === confirmPass;
  const isStrong = hasLength && hasUpper && hasLower && hasNumber && hasSpecial && isMatching;

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4 font-sans text-slate-800">
      <div className="w-full max-w-md">
        {/* Branding & Logo Header */}
        <div className="text-center mb-8">
          <img
            src="/nd.png"
            alt="NEXDESK Logo"
            className="w-16 h-16 rounded-2xl object-contain mx-auto mb-4 shadow-sm bg-white p-1 border border-slate-200"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = '/icon-192x192.png';
            }}
          />
          <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">
            NEXDESK
          </h1>
          <p className="text-sm font-medium text-slate-500 mt-1">
            Enterprise IT Helpdesk & Asset Management
          </p>
        </div>

        {/* Main Authentication Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
          {loginError && (
            <div className="mb-6 p-3.5 bg-red-50 border border-red-200 rounded-xl text-red-700 text-xs font-semibold flex items-center gap-2">
              <svg className="w-4 h-4 flex-shrink-0 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{loginError}</span>
            </div>
          )}

          {requiresPasswordChange ? (
            /* Mandatory First-Login Password Upgrade Step */
            <form onSubmit={handleForcePasswordChange} className="space-y-4">
              <div className="text-center mb-2">
                <div className="w-12 h-12 rounded-2xl bg-amber-50 text-amber-600 border border-amber-200 flex items-center justify-center mx-auto mb-3 shadow-2xs">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <h3 className="text-base font-bold text-slate-900">Security Upgrade Required</h3>
                <p className="text-xs text-slate-500 mt-1">
                  You are using the default setup password. You must set a new master password before accessing the platform.
                </p>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                  New Master Password
                </label>
                <PasswordInput
                  required
                  placeholder="Min 12 characters (A-Z, a-z, 0-9, symbol)"
                  value={newPass}
                  onChange={(e) => setForcePasswordForm({ ...forcePasswordForm, new_password: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                  Confirm Master Password
                </label>
                <PasswordInput
                  required
                  placeholder="Re-enter your new password"
                  value={confirmPass}
                  onChange={(e) => setForcePasswordForm({ ...forcePasswordForm, confirm_password: e.target.value })}
                />
              </div>

              {/* Live Password Complexity Checklist */}
              <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 space-y-1.5 text-[11px]">
                <div className="font-semibold text-slate-600 mb-1">Password Requirements:</div>
                <div className={`flex items-center gap-1.5 ${hasLength ? 'text-emerald-600 font-bold' : 'text-slate-400'}`}>
                  <span>{hasLength ? '✓' : '○'}</span>
                  <span>Minimum 12 characters</span>
                </div>
                <div className={`flex items-center gap-1.5 ${hasUpper && hasLower ? 'text-emerald-600 font-bold' : 'text-slate-400'}`}>
                  <span>{hasUpper && hasLower ? '✓' : '○'}</span>
                  <span>Uppercase & lowercase letters</span>
                </div>
                <div className={`flex items-center gap-1.5 ${hasNumber && hasSpecial ? 'text-emerald-600 font-bold' : 'text-slate-400'}`}>
                  <span>{hasNumber && hasSpecial ? '✓' : '○'}</span>
                  <span>Numbers & special symbols</span>
                </div>
                <div className={`flex items-center gap-1.5 ${isMatching ? 'text-emerald-600 font-bold' : 'text-slate-400'}`}>
                  <span>{isMatching ? '✓' : '○'}</span>
                  <span>Passwords match</span>
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading || !isStrong}
                className="w-full py-3 px-4 bg-amber-600 hover:bg-amber-700 active:bg-amber-800 text-white font-bold text-sm rounded-xl shadow-sm disabled:opacity-50 transition-all flex items-center justify-center gap-2 cursor-pointer"
              >
                {isLoading ? 'Securing Account...' : 'Upgrade Master Password & Continue'}
              </button>

              <button
                type="button"
                onClick={() => setRequiresPasswordChange(false)}
                className="text-xs font-semibold text-slate-500 hover:text-slate-800 hover:underline block w-full text-center pt-1"
              >
                Back to Login
              </button>
            </form>
          ) : !requiresTOTP && !requiresTOTPSetup ? (
            /* Primary Login Form */
            <form onSubmit={handleLogin} className="space-y-4">
              {/* Client Code Field */}
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                  Client Code
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    required
                    className="block w-full pl-10 pr-3.5 py-2.5 bg-slate-50 hover:bg-white focus:bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all"
                    placeholder="e.g. 031210"
                    value={loginData.client_code}
                    onChange={(e) => setLoginData({ ...loginData, client_code: e.target.value })}
                  />
                </div>
              </div>

              {/* Username Field */}
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                  Username
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    required
                    className="block w-full pl-10 pr-3.5 py-2.5 bg-slate-50 hover:bg-white focus:bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all"
                    placeholder="Enter your username"
                    value={loginData.username}
                    onChange={(e) => setLoginData({ ...loginData, username: e.target.value })}
                  />
                </div>
              </div>

              {/* Password Field */}
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                  Password
                </label>
                <PasswordInput
                  value={loginData.password}
                  onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                  placeholder="Enter your password"
                  required={true}
                  className="w-full"
                />
              </div>

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white font-bold text-sm rounded-xl shadow-sm disabled:opacity-50 transition-all flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Authenticating...</span>
                    </>
                  ) : (
                    <span>Sign In to Workspace</span>
                  )}
                </button>
              </div>
            </form>
          ) : requiresTOTP ? (
            /* TOTP Verification Step */
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="text-center mb-4">
                <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center mx-auto mb-2">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <h3 className="text-base font-bold text-slate-900">Two-Factor Authentication</h3>
                <p className="text-xs text-slate-500 mt-1">
                  {useBackupCode 
                    ? 'Enter one of your 8-digit emergency backup codes'
                    : 'Enter the 6-digit verification code from Google Authenticator'
                  }
                </p>
              </div>

              {!useBackupCode ? (
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                    Authenticator Code
                  </label>
                  <input
                    type="text"
                    maxLength="6"
                    required
                    autoFocus
                    className="block w-full py-3 bg-slate-50 focus:bg-white border border-slate-200 rounded-xl text-center text-xl font-mono font-bold tracking-widest text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all"
                    placeholder="000000"
                    value={totpToken}
                    onChange={(e) => setTotpToken(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  />
                </div>
              ) : (
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                    Backup Code
                  </label>
                  <input
                    type="text"
                    required
                    autoFocus
                    className="block w-full py-3 bg-slate-50 focus:bg-white border border-slate-200 rounded-xl text-center text-lg font-mono font-bold tracking-widest text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all"
                    placeholder="e.g. 12345678"
                    value={backupCode}
                    onChange={(e) => setBackupCode(e.target.value)}
                  />
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white font-bold text-sm rounded-xl shadow-sm disabled:opacity-50 transition-all flex items-center justify-center gap-2"
              >
                {isLoading ? 'Verifying...' : 'Verify & Continue'}
              </button>

              <div className="text-center pt-2 space-y-1.5">
                <button
                  type="button"
                  onClick={() => setUseBackupCode(!useBackupCode)}
                  className="text-xs font-semibold text-blue-600 hover:text-blue-700 hover:underline block w-full"
                >
                  {useBackupCode ? 'Use Google Authenticator Code' : 'Lost access? Use Backup Code'}
                </button>
                {resetTOTP && (
                  <button
                    type="button"
                    onClick={resetTOTP}
                    className="text-[11px] font-semibold text-slate-500 hover:text-slate-800 hover:underline block w-full pt-1"
                  >
                    Need to scan QR code? Reset Google Authenticator
                  </button>
                )}
              </div>
            </form>
          ) : (
            /* TOTP Setup Required Step */
            <div className="space-y-4">
              <div className="text-center mb-4">
                <h3 className="text-base font-bold text-slate-900">Setup Google Authenticator</h3>
                <p className="text-xs text-slate-500 mt-1">
                  SuperAdmin accounts require two-factor authentication setup before accessing the dashboard.
                </p>
              </div>

              {!totpSetupData ? (
                <div className="space-y-3">
                  <button
                    onClick={setupTOTP}
                    disabled={isLoading}
                    className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white font-bold text-sm rounded-xl shadow-sm disabled:opacity-50 transition-all"
                  >
                    {isLoading ? 'Generating Setup Key...' : 'Generate 2FA QR Code'}
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="text-center">
                    <p className="text-xs font-medium text-slate-600 mb-2">
                      Scan this QR code with Google Authenticator:
                    </p>
                    <div className="bg-white p-3 rounded-xl border border-slate-200 inline-block shadow-sm">
                      <img src={totpSetupData.qr_code} alt="2FA QR Code" className="w-44 h-44 mx-auto" />
                    </div>
                    <p className="text-[11px] text-slate-400 mt-2">
                      Or manual key: <code className="bg-slate-100 px-1.5 py-0.5 rounded text-slate-700 font-mono text-[11px]">{totpSetupData.secret}</code>
                    </p>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5 text-center">
                      Enter 6-Digit Code to Confirm
                    </label>
                    <input
                      type="text"
                      maxLength="6"
                      className="block w-full py-2.5 bg-slate-50 focus:bg-white border border-slate-200 rounded-xl text-center text-lg font-mono font-bold tracking-widest text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all"
                      placeholder="000000"
                      onChange={(e) => {
                        const token = e.target.value.replace(/\D/g, '').slice(0, 6);
                        e.target.value = token;
                        if (token.length === 6) {
                          verifyTOTPSetup(token);
                        }
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer Support Info */}
        <div className="text-center mt-6">
          <p className="text-xs text-slate-400 font-medium">
            Need help? Contact your administrator or email{' '}
            <a href="mailto:support@nexbond.in" className="text-blue-600 font-bold hover:underline">
              support@nexbond.in
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginView;
